import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { Input } from '@/components/ui'

/** Six boxes that auto-advance, accept paste, and submit on the sixth digit (03 §5.2). */
export function OtpInput({ value, onChange, onComplete, disabled }: { value: string; onChange: (v: string) => void; onComplete: (v: string) => void; disabled?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(6, ' ').slice(0, 6).split('')

  function setAt(i: number, d: string) {
    const next = (value.slice(0, i) + d + value.slice(i + 1)).replace(/\D/g, '').slice(0, 6)
    onChange(next)
    if (d && i < 5) refs.current[i + 1]?.focus()
    if (next.length === 6) onComplete(next)
  }

  function onPaste(e: ClipboardEvent) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!text) return
    e.preventDefault()
    onChange(text)
    refs.current[Math.min(text.length, 5)]?.focus()
    if (text.length === 6) onComplete(text)
  }

  function onKey(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i]?.trim() && i > 0) {
      refs.current[i - 1]?.focus()
      onChange(value.slice(0, i - 1))
    }
  }

  return (
    <div className="flex gap-2" onPaste={onPaste}>
      {digits.map((d, i) => (
        <Input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          aria-label={`Digit ${i + 1}`}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          autoFocus={i === 0}
          value={d.trim()}
          onChange={(e) => setAt(i, e.target.value.replace(/\D/g, '').slice(-1))}
          onKeyDown={(e) => onKey(i, e)}
          className="h-11 w-10 px-0 text-center font-mono text-lg md:text-lg"
        />
      ))}
    </div>
  )
}
